import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

interface SidebarContextValue {
  pinned: boolean;
  togglePinned: () => void;
  setPinned: (pinned: boolean) => void;
}

const STORAGE_KEY = 'telos_sidebar_pinned';

const SidebarContext = createContext<SidebarContextValue | null>(null);

function readInitialPinned(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
  } catch { /* ignore */ }
  return false;
}

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [pinned, setPinnedState] = useState<boolean>(readInitialPinned);

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, pinned ? 'true' : 'false'); } catch { /* ignore */ }
  }, [pinned]);

  // React to storage events from other tabs (and as a defensive sync mechanism)
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key !== STORAGE_KEY || e.newValue == null) return;
      setPinnedState(e.newValue === 'true');
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value: SidebarContextValue = {
    pinned,
    togglePinned: () => setPinnedState(p => !p),
    setPinned: (p: boolean) => setPinnedState(p),
  };

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebar(): SidebarContextValue {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error('useSidebar must be used within SidebarProvider');
  return ctx;
}
